/**
 * @typedef {import('./types').Breakpoint} Breakpoint
 * @typedef {import('./types').DeviceType} DeviceType
 * @typedef {import('./types').Viewport} Viewport
 */

/** @type {Record<DeviceType, Viewport>} */
const DEVICES = {
  mobile: 'xs',
  tablet: 'sm',
  desktop: 'xl',
}

// IMPORTANT
// Needs to by synced with "src/styles/_breakpoints.scss"
const SCREENS = {
  xs: 0,
  sm: 600,
  md: 960,
  lg: 1280,
  xl: 1920,
}

const VIEWPORTS = Object.keys(SCREENS)

/** @type {Breakpoint[]} */
const BREAKPOINTS = Object.values(SCREENS)

/**
 * @param {Viewport} currentViewport
 * @returns {{
 *   currentViewport: Viewport,
 *   is: (viewport: Viewport) => boolean,
 *   up: (viewport: Viewport) => boolean,
 *   down: (viewport: Viewport) => boolean,
 *   between: (from: Viewport, to: Viewport) => boolean
 * }}
 */
const createViewportHelpers = currentViewport => {
  const is = viewport => currentViewport === viewport

  const up = viewport => {
    const viewportIndex = VIEWPORTS.findIndex(key => key === viewport)
    return VIEWPORTS.slice(viewportIndex).includes(currentViewport)
  }

  const down = viewport => {
    const viewportIndex = VIEWPORTS.findIndex(key => key === viewport)
    return VIEWPORTS.slice(0, viewportIndex + 1).includes(currentViewport)
  }

  const between = (from, to) => {
    const fromIndex = VIEWPORTS.findIndex(key => key === from)
    const toIndex = VIEWPORTS.findIndex(key => key === to)

    const breakpointsList =
      fromIndex > toIndex ? VIEWPORTS.slice(toIndex, fromIndex + 1) : VIEWPORTS.slice(fromIndex, toIndex + 1)

    return breakpointsList.includes(currentViewport)
  }

  return { currentViewport, is, down, up, between }
}

/**
 * @param {Breakpoint} breakpoint
 * @returns {string}
 */
const getMediaQueryString = breakpoint => `(min-width: ${breakpoint}px)`

/**
 * @param {MediaQueryList[]} mediaQueryLists
 */
const getCurrentViewport = mediaQueryLists =>
  mediaQueryLists.reduce(
    (currentViewport, mediaQueryList, i) => (mediaQueryList.matches ? VIEWPORTS[i] : currentViewport),
    VIEWPORTS[0]
  )

/**
 * @param {string} deviceType
 * @returns {Viewport}
 */
const getSsrViewport = deviceType => DEVICES[deviceType] ?? DEVICES.desktop

export {
  SCREENS,
  VIEWPORTS,
  BREAKPOINTS,
  DEVICES,
  createViewportHelpers,
  getMediaQueryString,
  getCurrentViewport,
  getSsrViewport,
}
